import type { ComponentProps } from 'react';

import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { cn } from '@repo/lib/cn';
import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react';

export function Accordion(accordionProps: ComponentProps<typeof AccordionPrimitive.Root>) {
  const { className, ...props } = accordionProps;
  return <AccordionPrimitive.Root data-slot="accordion" className={cn('flex w-full flex-col', className)} {...props} />;
}

export function AccordionItem(accordionItemProps: ComponentProps<typeof AccordionPrimitive.Item>) {
  const { className, ...props } = accordionItemProps;
  return <AccordionPrimitive.Item data-slot="accordion-item" className={cn('ot-last:border-b', className)} {...props} />;
}

export function AccordionTrigger(accordionTriggerProps: ComponentProps<typeof AccordionPrimitive.Trigger>) {
  const { className, children, ...props } = accordionTriggerProps;
  return (
    <AccordionPrimitive.Header className="flex">
      <AccordionPrimitive.Trigger
        data-slot="accordion-trigger"
        className={cn(
          'outline-0 focus-visible:outline-3 cursor-pointer focus-visible:border-border-primary-focus **:data-[slot=accordion-trigger-icon]:text-muted-foreground rounded-md py-2.5 text-left text-sm font-medium hover:underline **:data-[slot=accordion-trigger-icon]:ml-auto **:data-[slot=accordion-trigger-icon]:size-4 group/accordion-trigger relative flex flex-1 items-start justify-between border border-transparent transition-all disabled:pointer-events-none disabled:opacity-50',
          className
        )}
        {...props}
      >
        {children}
        <ChevronDownIcon data-slot="accordion-trigger-icon" className="pointer-events-none shrink-0 group-aria-expanded/accordion-trigger:hidden" />
        <ChevronUpIcon data-slot="accordion-trigger-icon" className="pointer-events-none hidden shrink-0 group-aria-expanded/accordion-trigger:inline" />
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
  );
}

export function AccordionContent(accordionContentProps: ComponentProps<typeof AccordionPrimitive.Content>) {
  const { className, children, ...props } = accordionContentProps;
  return (
    <AccordionPrimitive.Content data-slot="accordion-content" className="data-open:animate-accordion-down data-closed:animate-accordion-up overflow-hidden text-sm" {...props}>
      <div className={cn('pt-0 pb-2.5 [&_a]:hover:text-foreground h-(--radix-accordion-content-height) [&_a]:underline [&_a]:underline-offset-3 [&_p:not(:last-child)]:mb-4', className)}>
        {children}
      </div>
    </AccordionPrimitive.Content>
  );
}
